import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * Simple full-screen image lightbox.
 * Props:
 *   src   - image URL to enlarge (opens when truthy, closes when null)
 *   alt   - alt text
 *   onClose - () => void
 */
export default function ImageLightbox({ src, alt = "", onClose }) {
  useEffect(() => {
    if (!src) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [src, onClose]);

  return createPortal(
    <AnimatePresence>
      {src && (
        <motion.div
          data-testid="image-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={alt || "Enlarged image"}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 sm:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="image-lightbox-close"
            aria-label="Close enlarged image"
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <X className="h-5 w-5" />
          </button>
          <motion.img
            src={src}
            alt={alt}
            data-testid="image-lightbox-img"
            initial={{ scale: 0.96 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.96 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[92vh] max-w-[95vw] object-contain rounded-lg shadow-2xl"
          />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
